/**
 * Admin Audit Log
 *
 * Read-only view of `admin.auditLog.list` — who changed what, and when. MUNI
 * requires an in-app trail of administrative actions (space, room, service,
 * membership, and user-role changes).
 *
 * Auth: ADMIN only — non-admins get an inline 403 view rather than a redirect,
 * the same pattern as `admin-users.tsx` and `admin-sessions.tsx`.
 */

import type { AppRouter } from '@polygraf/server/trpc'
import type { inferRouterOutputs } from '@trpc/server'
import { ScrollText, Shield } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import { Alert, Button, Spinner } from '@/components/ui'
import { usePageTitle } from '@/hooks/use-page-title'
import { useUserRole } from '@/hooks/use-user-role'
import { formatDate } from '@/lib/format-time'
import { trpc } from '@/trpc/client'

type AuditEntry = inferRouterOutputs<AppRouter>['admin']['auditLog']['list']['entries'][number]

const PAGE_SIZE = 50

/**
 * Render `details` as a compact "key: value" line. The payload is written by
 * the server-side recorder and holds only names, ids, and roles — never
 * secrets or service endpoints.
 */
function formatDetails(details: Record<string, unknown> | null): string {
	if (!details) return ''
	return Object.entries(details)
		.filter(([, value]) => value !== null && value !== undefined)
		.map(([key, value]) => `${key}: ${String(value)}`)
		.join(', ')
}

export default function AdminAuditLogPage() {
	const { t, i18n } = useTranslation()
	const location = useLocation()
	const { isAdmin, isLoading: roleLoading, error: roleError } = useUserRole()
	usePageTitle(!roleLoading && !isAdmin ? t('admin.accessDenied') : t('admin.auditLog.title'))

	const query = trpc.admin.auditLog.list.useInfiniteQuery(
		{ limit: PAGE_SIZE },
		{
			enabled: isAdmin,
			retry: false,
			getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
		},
	)

	if (roleLoading) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-8 flex justify-center opacity-30">
				<Spinner size="lg" />
			</div>
		)
	}

	// A transient role-check failure must not read as a revoked role — surface
	// the error, and send anonymous callers to sign-in with a returnTo.
	if (roleError) {
		const code = (roleError.data as { code?: string } | null | undefined)?.code
		if (code === 'UNAUTHORIZED') {
			const returnTo = `${location.pathname}${location.search}`
			return <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />
		}
		return (
			<div className="max-w-4xl mx-auto px-4 py-8">
				<Alert tone="error">{t('admin.roleCheckFailed')}</Alert>
			</div>
		)
	}

	if (!isAdmin) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-8">
				<div className="card-refined p-12 text-center">
					<Shield size={48} className="mx-auto mb-4 opacity-30" aria-hidden />
					<p className="text-lg font-medium">{t('admin.accessDenied')}</p>
					<p className="text-base-content/60 mt-2">{t('admin.adminOnly')}</p>
				</div>
			</div>
		)
	}

	if (query.isLoading) {
		return (
			<div className="flex justify-center py-12 text-primary">
				<Spinner size="lg" />
			</div>
		)
	}

	if (query.error) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-8">
				<Alert tone="error">{query.error.message}</Alert>
			</div>
		)
	}

	const entries: AuditEntry[] = query.data?.pages.flatMap((page) => page.entries) ?? []

	return (
		<div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
			<div>
				<h1 className="text-2xl font-semibold">{t('admin.auditLog.title')}</h1>
				<p className="text-base-content/60 mt-1">{t('admin.auditLog.subtitle')}</p>
			</div>

			{entries.length === 0 ? (
				<div className="card-refined p-12 text-center">
					<ScrollText size={40} className="mx-auto mb-3 opacity-30" aria-hidden />
					<p className="text-base-content/60">{t('admin.auditLog.empty')}</p>
				</div>
			) : (
				<div className="card-refined overflow-x-auto">
					<table className="table w-full" aria-label={t('admin.auditLog.title')}>
						<thead>
							<tr className="text-left text-xs uppercase tracking-wide text-base-content/60">
								<th scope="col" className="px-4 py-3">
									{t('admin.auditLog.columns.time')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.auditLog.columns.actor')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.auditLog.columns.action')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.auditLog.columns.target')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.auditLog.columns.details')}
								</th>
							</tr>
						</thead>
						<tbody>
							{entries.map((entry) => (
								<tr key={entry.id}>
									<td className="px-4 py-3 whitespace-nowrap text-sm text-base-content/70">
										{formatDate(entry.createdAt, i18n.language)}
									</td>
									<td className="px-4 py-3 text-sm">
										{entry.actorEmail || t('admin.auditLog.actorUnknown')}
									</td>
									<td className="px-4 py-3">
										<code className="text-xs">{entry.action}</code>
									</td>
									<td className="px-4 py-3 text-sm text-base-content/70">
										{entry.targetType}: {entry.targetId}
									</td>
									<td className="px-4 py-3 text-sm text-base-content/70 break-all">
										{formatDetails(entry.details)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{query.hasNextPage && (
				<div className="flex justify-center">
					<Button
						variant="ghost"
						loading={query.isFetchingNextPage}
						disabled={query.isFetchingNextPage}
						onClick={() => query.fetchNextPage()}
					>
						{t('admin.auditLog.loadMore')}
					</Button>
				</div>
			)}
		</div>
	)
}
